import { uiIcon } from "@agent/uiIcons";
import type {
  IconName,
  UiFieldItem,
  UiListItem,
  UiTone,
} from "@agent/uiSchema";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  cn,
  LedgerBody,
  LedgerCell,
  LedgerHead,
  LedgerHeadCell,
  LedgerRow,
  LedgerTable,
  StatusMark,
  type StatusMarkTone,
  Swipe,
  type SwipeTone,
} from "@scribenote/brian-design-system";
import type { ReactNode } from "react";

/** Presentational a2ui pieces: they emit a node's `action`; the dock decides what it means. */

interface ToneStyle {
  /** Empty inherits. */
  text: string;
  /** Empty inherits. */
  icon: string;
  /** Empty means untinted. */
  surface: string;
  /** `null` means `border-rule`. */
  border: string | null;
  /** `null`: the word takes `text` ink; blue's wash is reserved for "Brian did this". */
  wash: SwipeTone | null;
  /** Fallback icon so a tone is never carried by colour alone. */
  toneIcon: IconName | undefined;
}

// `info` is generic emphasis, so it takes the indigo accent (`accent-deep` for AA small text), never Brian's blue.
const TONE_STYLES: Record<UiTone, ToneStyle> = {
  neutral: {
    text: "",
    icon: "",
    surface: "",
    border: null,
    wash: null,
    toneIcon: undefined,
  },
  info: {
    text: "text-accent-deep",
    icon: "text-accent-deep",
    surface: "bg-accent/8",
    border: "border-accent",
    wash: null,
    toneIcon: "circle-info",
  },
  success: {
    text: "text-ink-working",
    icon: "text-ink-working",
    surface: "bg-ink-working/8",
    border: "border-ink-working",
    wash: "working",
    toneIcon: "circle-check",
  },
  warning: {
    text: "text-ink-flag",
    icon: "text-ink-flag",
    surface: "bg-ink-flag/8",
    border: "border-ink-flag",
    wash: "flag",
    toneIcon: "triangle-exclamation",
  },
  urgent: {
    text: "text-ink-stopped",
    icon: "text-ink-stopped",
    surface: "bg-ink-stopped/8",
    border: "border-ink-stopped",
    wash: "stopped",
    toneIcon: "circle-exclamation",
  },
};

export const toneStyle = (tone: UiTone | undefined): ToneStyle =>
  TONE_STYLES[tone ?? "neutral"] ?? TONE_STYLES.neutral;

const UiIcon = ({
  name,
  className,
}: {
  name: IconName | undefined;
  className?: string;
}) => {
  const icon = uiIcon(name);
  if (!icon) return null;
  return <FontAwesomeIcon icon={icon} className={className} aria-hidden />;
};

export const CardFrame = ({
  id,
  children,
}: {
  id: string;
  children: ReactNode;
}) => (
  // Opaque so the tree reads the same on any surface.
  <div
    data-testid={`ui-card-${id}`}
    className="rounded-paper border border-rule bg-card p-3 font-body text-ink-body shadow-lift"
  >
    <div className="flex flex-col gap-2">{children}</div>
  </div>
);

/** Container query, not a breakpoint: the same tree renders in the narrow dock and wide reader. */
export const GridBlock = ({
  columns,
  children,
}: {
  columns: 2 | 3;
  children: ReactNode;
}) => (
  <div className="@container w-full" data-testid={`ui-grid-${columns}`}>
    {/* Spelled out per count: Tailwind only emits classes literal in source. */}
    <div
      className={
        columns === 3
          ? "grid grid-cols-1 gap-3 @md:grid-cols-2 @2xl:grid-cols-3"
          : "grid grid-cols-1 gap-3 @md:grid-cols-2"
      }
    >
      {children}
    </div>
  </div>
);

// Icon-only rows stay a `div` (axe `empty-heading`); titles stay text-ink so tone shows on icon/border.
const PANEL_TITLE_ROW =
  "flex items-start gap-2 pb-1 font-display text-lg font-bold text-ink";

// h-7 is text-lg's line box, centring the glyph on a wrapped title's first line.
const PANEL_TITLE_ICON = "inline-flex h-7 shrink-0 items-center text-base";

const PanelTitle = ({
  title,
  icon,
  iconClassName,
}: {
  title?: string;
  icon?: IconName;
  iconClassName: string;
}) => {
  const iconNode = uiIcon(icon) ? (
    <span className={cn(PANEL_TITLE_ICON, iconClassName)}>
      <UiIcon name={icon} />
    </span>
  ) : undefined;
  if (!title) {
    return iconNode ? <div className={PANEL_TITLE_ROW}>{iconNode}</div> : null;
  }
  return (
    <h3 className={PANEL_TITLE_ROW}>
      {iconNode}
      <span className="min-w-0 break-words">{title}</span>
    </h3>
  );
};

/** A toned section shows on its edge and icon, never as a fill. */
export const PanelSection = ({
  title,
  icon,
  tone,
  children,
}: {
  title?: string;
  icon?: IconName;
  tone?: UiTone;
  children: ReactNode;
}) => {
  const style = toneStyle(tone);
  return (
    <section
      data-testid="ui-section"
      className={cn(
        "flex flex-col gap-2 rounded-paper border bg-paper p-3",
        style.border ?? "border-rule",
      )}
    >
      <PanelTitle
        title={title}
        icon={icon ?? style.toneIcon}
        iconClassName={style.icon || "text-annotation"}
      />
      {children}
    </section>
  );
};

/** Tint sits on its own opaque `bg-card`, so it holds its hue on any backdrop. */
export const Callout = ({
  tone,
  title,
  icon,
  children,
}: {
  tone: UiTone;
  title?: string;
  icon?: IconName;
  children: ReactNode;
}) => {
  const style = toneStyle(tone);
  return (
    <div
      data-testid={`ui-callout-${tone}`}
      className={cn(
        "overflow-hidden rounded-paper border bg-card",
        style.border ?? "border-rule",
      )}
    >
      <div className={cn("flex flex-col gap-2 p-3", style.surface)}>
        <PanelTitle
          title={title}
          icon={icon ?? style.toneIcon}
          iconClassName={style.icon || "text-annotation"}
        />
        {children}
      </div>
    </div>
  );
};

const toCell = (value: unknown): string =>
  typeof value === "string" ? value : value == null ? "" : String(value);

/** The value track flexes: `[1fr_auto]` let a long value zero the label track. */
export const FieldList = ({ items }: { items: UiFieldItem[] }) => {
  const rows = (Array.isArray(items) ? items : []).filter(
    (item): item is UiFieldItem => item != null && typeof item === "object",
  );
  if (rows.length === 0) return null;

  return (
    <dl data-testid="ui-fields" className="@container flex w-full flex-col">
      {rows.map((item, i) => {
        const style = toneStyle(item.tone);
        const iconName = item.icon ?? style.toneIcon;
        return (
          <div
            key={i}
            data-testid={`ui-field-${i}`}
            className={cn(
              "grid grid-cols-1 items-baseline gap-x-3 py-2 @xs:grid-cols-[fit-content(50%)_minmax(0,1fr)]",
              i > 0 && "border-t border-rule",
            )}
          >
            <dt className="min-w-0 break-words text-annotation-caps text-annotation">
              {toCell(item.label)}
            </dt>
            <dd
              className={cn(
                "flex min-w-0 items-baseline gap-2 font-mono text-sm font-medium @xs:justify-end @xs:text-right",
                style.text || "text-ink",
              )}
            >
              {uiIcon(iconName) && (
                <span
                  className={cn(
                    "inline-flex shrink-0",
                    style.icon || "text-annotation",
                  )}
                >
                  <UiIcon name={iconName} />
                </span>
              )}
              <span className="min-w-0 break-words">{toCell(item.value)}</span>
            </dd>
            {item.note && (
              <dd className="col-span-full min-w-0 break-words pt-0.5 text-xs text-annotation">
                {toCell(item.note)}
              </dd>
            )}
          </div>
        );
      })}
    </dl>
  );
};

export const ItemList = ({
  items,
  divided = false,
}: {
  items: UiListItem[];
  divided?: boolean;
}) => {
  const rows = (Array.isArray(items) ? items : []).filter(
    (item): item is UiListItem => item != null && typeof item === "object",
  );
  if (rows.length === 0) return null;

  return (
    <ul
      data-testid="ui-list"
      className={cn("flex flex-col", !divided && "gap-2")}
    >
      {rows.map((item, i) => {
        const style = toneStyle(item.tone);
        const iconName = item.icon ?? style.toneIcon;
        return (
          <li
            key={i}
            data-testid={`ui-list-item-${i}`}
            className={cn(
              "flex items-start gap-2",
              divided && "py-2",
              divided && i > 0 && "border-t border-rule",
            )}
          >
            {uiIcon(iconName) && (
              <span
                className={cn(
                  "mt-0.5 inline-flex shrink-0",
                  style.icon || "text-annotation",
                )}
              >
                <UiIcon name={iconName} />
              </span>
            )}
            <span className="min-w-0 flex-1 break-words text-sm text-ink-body">
              {toCell(item.text)}
            </span>
            {item.badge &&
              (style.wash ? (
                <Swipe
                  tone={style.wash}
                  data-testid={`ui-list-badge-${i}`}
                  className="inline-flex shrink-0 items-center text-annotation-caps"
                >
                  {toCell(item.badge)}
                </Swipe>
              ) : (
                <span
                  data-testid={`ui-list-badge-${i}`}
                  className={cn(
                    "shrink-0 text-annotation-caps",
                    style.text || "text-annotation",
                  )}
                >
                  {toCell(item.badge)}
                </span>
              ))}
          </li>
        );
      })}
    </ul>
  );
};

export const DividerRule = () => (
  <hr data-testid="ui-divider" className="border-t border-rule" />
);

// `blue` can't claim the "Brian did this" wash, so it takes accent ink.
const BADGE_LOOK: Record<
  string,
  { wash: SwipeTone | null; mark: StatusMarkTone; look: string }
> = {
  red: { wash: "stopped", mark: "neutral", look: "stopped" },
  orange: { wash: "flag", mark: "neutral", look: "flag" },
  yellow: { wash: "flag", mark: "neutral", look: "flag" },
  green: { wash: "working", mark: "neutral", look: "working" },
  blue: { wash: null, mark: "accent", look: "accent" },
  gray: { wash: null, mark: "neutral", look: "neutral" },
  default: { wash: null, mark: "accent", look: "accent" },
};

export const UiBadge = ({
  variant,
  icon,
  text,
  dataTestId,
}: {
  variant: string | undefined;
  icon?: IconName;
  text: string;
  dataTestId: string;
}) => {
  const look = BADGE_LOOK[variant ?? "default"] ?? BADGE_LOOK.default;
  // An empty icon slot would still take its gap.
  const glyph = uiIcon(icon) ? <UiIcon name={icon} /> : undefined;

  return look.wash ? (
    <Swipe
      tone={look.wash}
      icon={glyph}
      data-testid={dataTestId}
      data-variant={look.look}
      className="shrink-0 text-annotation-caps"
    >
      {text}
    </Swipe>
  ) : (
    <StatusMark
      tone={look.mark}
      icon={glyph}
      data-testid={dataTestId}
      data-variant={look.look}
      className="shrink-0"
    >
      {text}
    </StatusMark>
  );
};

/** Defensive about shape so an odd payload can't fail the card; ragged rows pad to the widest. */
export const TableBlock = ({
  columns,
  rows,
}: {
  columns: string[];
  rows: string[][];
}) => {
  const header = Array.isArray(columns) ? columns.map(toCell) : [];
  const body = (Array.isArray(rows) ? rows : [])
    .filter(Array.isArray)
    .map((row) => row.map(toCell));
  const colCount = Math.max(header.length, 0, ...body.map((row) => row.length));

  if (colCount === 0) return null;

  return (
    <div data-testid="ui-table" className="min-w-0">
      <LedgerTable>
        {header.length > 0 && (
          <LedgerHead>
            <tr>
              {Array.from({ length: colCount }, (_, i) => (
                <LedgerHeadCell key={i}>{header[i] ?? ""}</LedgerHeadCell>
              ))}
            </tr>
          </LedgerHead>
        )}
        <LedgerBody>
          {body.map((row, ri) => (
            <LedgerRow key={ri}>
              {Array.from({ length: colCount }, (_, ci) => (
                <LedgerCell key={ci}>{row[ci] ?? ""}</LedgerCell>
              ))}
            </LedgerRow>
          ))}
        </LedgerBody>
      </LedgerTable>
    </div>
  );
};

/** A `button` node with a `payload` or `description`. */
export const OptionButton = ({
  text,
  description,
  disabled,
  dataTestId,
  onPress,
}: {
  text: string;
  description?: string;
  disabled: boolean;
  dataTestId: string;
  onPress: () => void;
}) => (
  <button
    type="button"
    onClick={onPress}
    disabled={disabled}
    data-testid={dataTestId}
    className={cn(
      "flex w-full items-center justify-between gap-2 rounded-paper border border-rule px-3 py-2 text-left",
      "transition duration-150 ease-paper motion-reduce:transition-none",
      "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent",
      // `cn` has no tailwind-merge, so pick one surface rather than rely on stylesheet order.
      disabled
        ? "cursor-not-allowed bg-recess"
        : "bg-card hover:-translate-y-px hover:shadow-lift-lg",
    )}
  >
    <span className="flex min-w-0 flex-col">
      <span
        className={cn(
          "font-body text-sm font-medium",
          disabled ? "text-faint" : "text-ink",
        )}
      >
        {text}
      </span>
      {description && (
        <span
          className={cn("text-sm", disabled ? "text-faint" : "text-annotation")}
        >
          {description}
        </span>
      )}
    </span>
  </button>
);
